import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Anchor, Badge, Center, Loader, Stack, Table, Text } from '@mantine/core';
import { IconWorld } from '@tabler/icons-react';
import { api } from '../../lib/api';
import { DOMAINS_QUERY_KEY, autoRenewLabel } from '../../lib/api/domains/autodnsDomains';
import { DnsHostBadge, DomainExpiry } from '../../components/domains/DomainParts';
import { InfoEmpty } from './CustomerInfoParts';

/**
 * Kunden-Fenster, Reiter „Domains“ (v1.409.0, intern): bestätigt zugeordnete Domains aus AutoDNS plus
 * offene Vorschläge für diesen Kunden. Details und Verlauf auf der Domain-Seite.
 */
export function CustomerDomainsTab({ customerId, active }: { customerId: string; active: boolean }) {
  const { data, isLoading } = useQuery({
    queryKey: [...DOMAINS_QUERY_KEY, 'customer', customerId],
    queryFn: () => api.listDomains({ customerId, missing: true }),
    enabled: active,
  });
  const suggested = useQuery({
    queryKey: [...DOMAINS_QUERY_KEY, 'customer-suggested', customerId],
    queryFn: async () => (await api.listDomains({ status: ['SUGGESTED'] })).domains.filter((d) => d.assignment.suggestedCustomer?.id === customerId),
    enabled: active,
  });

  if (isLoading) return <Center py="xl"><Loader size="sm" /></Center>;
  const domains = data?.domains ?? [];
  if (domains.length === 0 && !suggested.data?.length) {
    return <InfoEmpty icon={<IconWorld size={20} />}>Keine Domains zugeordnet</InfoEmpty>;
  }
  return (
    <Stack gap="sm">
      {domains.length > 0 && (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Domain</Table.Th>
              <Table.Th>Ablauf</Table.Th>
              <Table.Th>Auto-Renew</Table.Th>
              <Table.Th>DNS bei</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {domains.map((d) => (
              <Table.Tr key={d.id}>
                <Table.Td>
                  <Anchor component={Link} to={`/domains/${d.id}`}>{d.idn ?? d.name}</Anchor>
                  {!d.presentInAccount && <Badge ml={6} size="xs" color="gray">nicht mehr im Konto</Badge>}
                </Table.Td>
                <Table.Td><DomainExpiry domain={d} /></Table.Td>
                <Table.Td>{autoRenewLabel(d)}</Table.Td>
                <Table.Td><DnsHostBadge host={d.dnsHost} /></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      {!!suggested.data?.length && (
        <Text size="sm" c="dimmed">
          Offene Vorschläge für diesen Kunden: {suggested.data.map((d) => d.name).join(', ')} — Freigabe unter{' '}
          <Anchor component={Link} to="/domains">Domains</Anchor>.
        </Text>
      )}
    </Stack>
  );
}
